/**
 * Custom Cart Drawer Component (Section Version) — mobile-fixed
 * Handles dynamic offer thresholds, progress bar, cart operations,
 * and special order instructions synced to Shopify cart note.
 *
 * Mobile fixes:
 *  - Drawer is portaled to <body> so position:fixed is never trapped by a
 *    transformed / filtered / contained ancestor (Horizon header/sections).
 *  - Cart-icon taps are intercepted in the CAPTURE phase on window, so the
 *    theme's own cart handlers (native <dialog> drawer / /cart link) never run.
 *  - Touch taps are only treated as taps (not scroll ends), and the synthetic
 *    "ghost click" after opening can no longer hit the close button/overlay.
 *  - Stricter cart-trigger detection (no longer hijacks search/account icons).
 */
class CustomCartDrawer extends HTMLElement {
    constructor() {
        super();
        this.classes = {
            active: 'active',
            loading: 'is-loading'
        };
        this.removedVariantIds = new Set(); // Track user-removed gifts
        this.lastUnlockedCount = 0;         // Track milestones for confetti
        this.firstLoad = true;              // Suppress confetti on initial load

        this._isPortaling = false;
        this._listenersBound = false;
        this._openedAt = 0;
        this._cartOpenLock = false;
        this._touchStart = null;
        this._suppressClickUntil = 0;

        // Bind once so add/removeEventListener use the same references
        this.open = this.open.bind(this);
        this.close = this.close.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.changeQty = this.changeQty.bind(this);
        this.handleCartLinesUpdate = this.handleCartLinesUpdate.bind(this);
        this.handleCartUpdateEvent = this.handleCartUpdateEvent.bind(this);
        this.handleTriggerClick = this.handleTriggerClick.bind(this);
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
    }

    connectedCallback() {
        // Ignore the disconnect/connect pair caused by our own move to <body>
        if (this._isPortaling) return;

        // ── Portal to <body> ───────────────────────────
        if (this.parentElement !== document.body) {
            // Remove stale copies (e.g. after a theme-editor section re-render)
            document.querySelectorAll('body > custom-cart-drawer').forEach((el) => {
                if (el !== this) el.remove();
            });
            this._isPortaling = true;
            document.body.appendChild(this);
            this._isPortaling = false;
        }

        if (this._listenersBound) return;
        this._listenersBound = true;

        document.addEventListener('custom-cart:open', this.open);
        document.addEventListener('cart:update', this.handleCartUpdateEvent);
        document.addEventListener('shopify:cart:lines-update', this.handleCartLinesUpdate);

        // Capture phase on window = runs before ANY theme handler
        window.addEventListener('click', this.handleTriggerClick, true);
        window.addEventListener('touchstart', this.handleTouchStart, { capture: true, passive: true });
        window.addEventListener('touchend', this.handleTouchEnd, { capture: true, passive: false });

        this.addEventListener('click', this.handleClick);

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
        } else {
            this.init();
        }
    }

    disconnectedCallback() {
        if (this._isPortaling) return;
        this._listenersBound = false;

        document.removeEventListener('custom-cart:open', this.open);
        document.removeEventListener('cart:update', this.handleCartUpdateEvent);
        document.removeEventListener('shopify:cart:lines-update', this.handleCartLinesUpdate);
        window.removeEventListener('click', this.handleTriggerClick, true);
        window.removeEventListener('touchstart', this.handleTouchStart, { capture: true });
        window.removeEventListener('touchend', this.handleTouchEnd, { capture: true });
        this.removeEventListener('click', this.handleClick);

        document.body.style.overflow = '';
        document.body.classList.remove('cart-drawer-open');
    }

    // ─────────────────────────────────────────────
    //  CART ICON TRIGGER DETECTION
    // ─────────────────────────────────────────────

    getCartTrigger(rawTarget) {
        const target = rawTarget instanceof Element ? rawTarget : rawTarget?.parentElement;
        if (!target || typeof target.closest !== 'function') return null;

        // Never treat taps inside our own drawer as triggers
        if (target.closest('custom-cart-drawer')) return null;

        // 1. Explicit cart elements
        const direct = target.closest([
            'cart-icon',
            '.cart-link',
            '.header-cart',
            '.header-actions__cart-icon',
            '.action__cart',
            '.cart-icon-wrapper',
            '[aria-controls="cart-drawer"]',
            '[data-testid="cart-drawer-trigger"]',
            'cart-drawer-component > button'
        ].join(', '));
        if (direct) return direct.closest('a, button') || direct;

        // 2. Generic header action buttons — only if they contain a cart icon
        const action = target.closest('.header-actions__action');
        if (action && action.querySelector('cart-icon, .cart-bubble, [class*="cart"]')) {
            return action;
        }

        // 3. Links pointing exactly at /cart (not /cart/add, /cart/change, etc.)
        const link = target.closest('a[href]');
        if (link) {
            try {
                const url = new URL(link.href, window.location.origin);
                const path = url.pathname.replace(/\/+$/, '');
                if (url.origin === window.location.origin && /(^|\/)cart$/.test(path)) {
                    return link;
                }
            } catch (err) {}
        }

        return null;
    }

    handleTouchStart(e) {
        const t = e.touches && e.touches[0];
        this._touchStart = t ? { x: t.clientX, y: t.clientY, time: Date.now() } : null;
    }

    handleTouchEnd(e) {
        const start = this._touchStart;
        this._touchStart = null;
        if (!start) return;

        // Only real taps — ignore scroll/swipe gestures and long presses
        const t = e.changedTouches && e.changedTouches[0];
        if (!t) return;
        const moved = Math.abs(t.clientX - start.x) > 10 || Math.abs(t.clientY - start.y) > 10;
        if (moved || Date.now() - start.time > 800) return;

        const trigger = this.getCartTrigger(e.target);
        if (!trigger) return;

        // Cancel the follow-up synthetic click (prevents theme handlers + ghost click)
        if (e.cancelable) e.preventDefault();
        e.stopImmediatePropagation();
        this._suppressClickUntil = Date.now() + 800;

        this.triggerOpen();
    }

    handleTriggerClick(e) {
        const trigger = this.getCartTrigger(e.target);
        if (!trigger) return;

        // Stop the theme's own cart drawer / navigation from running
        e.preventDefault();
        e.stopImmediatePropagation();

        // A touch already opened the drawer — swallow the duplicate click
        if (Date.now() < this._suppressClickUntil) return;

        this.triggerOpen();
    }

    triggerOpen() {
        if (this._cartOpenLock) return;
        this._cartOpenLock = true;
        setTimeout(() => { this._cartOpenLock = false; }, 400);
        this.open();
    }

    // ─────────────────────────────────────────────
    //  EXTERNAL CART EVENTS
    // ─────────────────────────────────────────────

    async handleCartUpdateEvent(e) {
        if (this.isAutoOperating) return;
        if (e.detail?.data?.source === 'custom-cart-drawer') return;
        await this.fetchCart();
        this.open();
    }

    async handleCartLinesUpdate(e) {
        if (this.isAutoOperating) return;
        if (e.detail?.source === 'custom-cart-drawer') return;

        // Await the deferred promise (Horizon CartLinesUpdateEvent) before fetching,
        // otherwise fetchCart() can run before Shopify finishes adding the item.
        const promise = e.promise || e.detail?.promise;
        if (promise) {
            try {
                const res = await promise;
                if (res?.detail?.didError) return;
            } catch (err) {
                console.error('CCD: Cart lines update promise rejected', err);
                return;
            }
        }

        await this.fetchCart();
        this.open();
    }

    init() {
        this.drawer          = this.querySelector('.ccd-drawer');
        this.overlay         = this.querySelector('.ccd-overlay');
        this.progressBar     = this.querySelector('.ccd-progress-fill');
        this.progressText    = this.querySelector('.ccd-progress-text');
        this.itemsContainer  = this.querySelector('.ccd-items-wrapper');
        this.milestoneLayer  = this.querySelector('.ccd-milestones-layer');

        this.offers = this.getOffers();

        if (this.offers.length > 0) {
            this.offers.sort((a, b) => a.threshold - b.threshold);
            this.renderMilestones();
        } else {
            setTimeout(() => {
                this.offers = this.getOffers();
                if (this.offers.length > 0) {
                    this.offers.sort((a, b) => a.threshold - b.threshold);
                    this.renderMilestones();
                    if (this.lastCart) this.updateProgress(this.lastCart);
                }
            }, 500);
        }

        this.fetchCart();
        this.initCoupons();
        this.initSpecialInstructions();
        this.bindFooterEvents();
    }

    // ─────────────────────────────────────────────
    //  SPECIAL INSTRUCTIONS
    // ─────────────────────────────────────────────

    initSpecialInstructions() {
        const toggle      = this.querySelector('#ccd-instructions-toggle');
        const body        = this.querySelector('#ccd-instructions-body');
        const textarea    = this.querySelector('#ccd-note-input');
        const saveBtn     = this.querySelector('#ccd-save-note');
        const savedMsg    = this.querySelector('#ccd-note-saved');
        const errorMsg    = this.querySelector('#ccd-note-error');
        const hiddenInput = this.querySelector('#ccd-note-hidden');

        if (!toggle || !textarea) return;
        // Guard against double-binding on the same elements
        if (toggle.dataset.ccdBound === '1') return;
        toggle.dataset.ccdBound = '1';

        // Priority: Shopify cart note (via hidden field set in Liquid) → sessionStorage fallback
        const liquidNote    = hiddenInput ? hiddenInput.value.trim() : '';
        const sessionNote   = sessionStorage.getItem('ccd_cart_note') || '';
        const restoredNote  = liquidNote || sessionNote;

        if (restoredNote) {
            textarea.value = restoredNote;
            if (hiddenInput) hiddenInput.value = restoredNote;
            this._openInstructions(toggle, body);
        }

        toggle.addEventListener('click', () => {
            if (body.classList.contains('open')) {
                this._closeInstructions(toggle, body);
            } else {
                this._openInstructions(toggle, body);
            }
        });

        textarea.addEventListener('input', () => {
            if (hiddenInput) hiddenInput.value = textarea.value;
            if (savedMsg) savedMsg.style.display = 'none';
            if (errorMsg) errorMsg.style.display = 'none';
        });

        if (saveBtn) {
            const saveLabel = saveBtn.textContent;
            saveBtn.addEventListener('click', async () => {
                const note = textarea.value.trim();
                saveBtn.disabled = true;
                saveBtn.textContent = 'Saving...';
                if (savedMsg) savedMsg.style.display = 'none';
                if (errorMsg) errorMsg.style.display = 'none';

                try {
                    const res = await fetch('/cart/update.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ note: note })
                    });

                    if (!res.ok) throw new Error('Cart update failed');

                    sessionStorage.setItem('ccd_cart_note', note);
                    if (hiddenInput) hiddenInput.value = note;

                    if (savedMsg) {
                        savedMsg.style.display = 'inline';
                        setTimeout(() => { savedMsg.style.display = 'none'; }, 3000);
                    }
                } catch (e) {
                    console.error('CCD: Note save failed', e);
                    if (errorMsg) {
                        errorMsg.style.display = 'inline';
                        setTimeout(() => { errorMsg.style.display = 'none'; }, 4000);
                    }
                } finally {
                    saveBtn.disabled = false;
                    saveBtn.textContent = saveLabel;
                }
            });
        }
    }

    _openInstructions(toggle, body) {
        body.classList.add('open');
        toggle.classList.add('open');
    }

    _closeInstructions(toggle, body) {
        body.classList.remove('open');
        toggle.classList.remove('open');
    }

    // ─────────────────────────────────────────────
    //  FOOTER / CHECKOUT
    // ─────────────────────────────────────────────

    bindFooterEvents() {
        const checkoutForm = this.querySelector('#ccd-cart-form');
        if (!checkoutForm || checkoutForm.dataset.ccdBound === '1') return;
        checkoutForm.dataset.ccdBound = '1';

        checkoutForm.addEventListener('submit', (e) => {
            const activeCode = sessionStorage.getItem('ccd_discount_code');
            if (activeCode) {
                e.preventDefault();
                const textarea    = this.querySelector('#ccd-note-input');
                const hiddenInput = this.querySelector('#ccd-note-hidden');
                if (textarea && hiddenInput) hiddenInput.value = textarea.value;

                const note = hiddenInput ? hiddenInput.value : '';
                let url = `/checkout?discount=${encodeURIComponent(activeCode)}`;
                if (note) url += `&note=${encodeURIComponent(note)}`;
                window.location.href = url;
            }
        });
    }

    // ─────────────────────────────────────────────
    //  OFFERS / MILESTONES
    // ─────────────────────────────────────────────

    getOffers() {
        try {
            const script = this.querySelector('#ccd-offers-data');
            if (!script) return [];
            return JSON.parse(script.textContent);
        } catch (e) {
            console.error('CCD: Error parsing offers', e);
            return [];
        }
    }

    renderMilestones() {
        if (!this.milestoneLayer || !this.offers.length) return;
        this.milestoneLayer.innerHTML = '';

        const count         = this.offers.length;
        const widthPerBlock = 100 / count;

        this.offers.forEach((offer, index) => {
            const percent = (index + 1) * widthPerBlock;
            const el      = document.createElement('div');
            el.className         = 'ccd-milestone';
            el.style.left        = `${percent}%`;
            el.dataset.threshold = offer.threshold;

            const moneyVal = 'Rs. ' + (offer.threshold / 100).toLocaleString();

            let iconHtml = '';
            if (offer.icon && !offer.icon.includes('no-image')) {
                iconHtml = `<img src="${offer.icon}" class="icon-default" style="width:100%; height:100%; object-fit:contain; border-radius:50%;" alt="Reward">`;
            } else {
                iconHtml = `<svg class="icon-default" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>`;
            }

            el.innerHTML = `
                <div class="ccd-milestone-label-top">${offer.rewardText}</div>
                <div class="ccd-milestone-icon">
                   ${iconHtml}
                   <svg class="icon-check" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" style="display:none;"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </div>
                <div class="ccd-milestone-label-bottom">${moneyVal}</div>
            `;
            this.milestoneLayer.appendChild(el);
        });
    }

    // ─────────────────────────────────────────────
    //  EVENT HANDLING (inside drawer)
    // ─────────────────────────────────────────────

    handleClick(e) {
        const target = e.target instanceof Element ? e.target : e.target?.parentElement;
        if (!target) return;

        if (target.closest('.ccd-close-btn') || target.closest('.ccd-overlay')) {
            // Ignore the ghost click that mobile browsers fire right after the
            // opening tap — it lands on the close button / overlay.
            if (Date.now() - this._openedAt < 600) return;
            this.close();
            return;
        }

        const removeBtn = target.closest('.ccd-remove-btn');
        if (removeBtn) {
            e.preventDefault();
            this.changeQty(parseInt(removeBtn.dataset.line, 10), 0);
            return;
        }

        const useCoupon = target.closest('.ccd-use-coupon');
        if (useCoupon) {
            e.preventDefault();
            const code  = useCoupon.dataset.code;
            const input = this.querySelector('#ccd-coupon-input');
            if (input) input.value = code;
            this.applyCoupon(code);
        }
    }

    // ─────────────────────────────────────────────
    //  DRAWER OPEN / CLOSE / TOGGLE
    // ─────────────────────────────────────────────

    toggle() {
        if (this.classList.contains(this.classes.active)) {
            this.close();
        } else {
            this.open();
        }
    }

    open() {
        if (!this.drawer) {
            console.warn('CCD: Drawer not found, initializing...');
            this.init();
        }
        if (this.drawer || this.querySelector('.ccd-drawer')) {
            if (!this.classList.contains(this.classes.active)) {
                this._openedAt = Date.now();
            }
            this.classList.add(this.classes.active);
            this.setAttribute('open', '');
            document.body.style.overflow = 'hidden';
            document.body.classList.add('cart-drawer-open');
        }
    }

    close() {
        this.classList.remove(this.classes.active);
        this.removeAttribute('open');
        document.body.style.overflow = '';
        document.body.classList.remove('cart-drawer-open');
    }

    // ─────────────────────────────────────────────
    //  CART FETCH & RE-RENDER
    // ─────────────────────────────────────────────

    async fetchCart() {
        try {
            const timestamp = Date.now();
            const res  = await fetch(`/cart.js?_t=${timestamp}`);
            const cart = await res.json();
            this.lastCart = cart;

            try {
                const sectionUrl  = `/?section_id=${this.dataset.sectionId}&_t=${timestamp}`;
                const sectionRes  = await fetch(sectionUrl);
                const sectionText = await sectionRes.text();

                const doc      = new DOMParser().parseFromString(sectionText, 'text/html');
                const newItems = doc.querySelector('.ccd-items-wrapper');
                if (newItems && this.itemsContainer) {
                    this.itemsContainer.innerHTML = newItems.innerHTML;
                }

                const newFooter     = doc.querySelector('.ccd-footer');
                const currentFooter = this.querySelector('.ccd-footer');
                if (newFooter && currentFooter) {
                    currentFooter.innerHTML = newFooter.innerHTML;
                    this.bindFooterEvents();
                }

                this.initCoupons();
                this.initSpecialInstructions();

                // Let the inline coupon-fix script in the section re-bind
                document.dispatchEvent(new CustomEvent('cart:rerender'));

            } catch (htmlErr) {
                console.warn('CCD: HTML fetch error', htmlErr);
            }

            this.updateProgress(cart);

        } catch (err) {
            console.error('CCD: FetchCart error', err);
        }
    }

    // ─────────────────────────────────────────────
    //  QUANTITY CHANGE
    // ─────────────────────────────────────────────

    async changeQty(line, qty) {
        const rows = this.querySelectorAll('.ccd-item');
        if (rows[line - 1]) rows[line - 1].style.opacity = '0.5';

        if (qty === 0 && this.lastCart && this.lastCart.items[line - 1]) {
            const variantId = this.lastCart.items[line - 1].variant_id;
            this.removedVariantIds.add(variantId);
        }

        try {
            await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ line: line, quantity: qty })
            });
            this.fetchCart();
        } catch (e) {
            console.error('CCD: Qty change error', e);
            if (rows[line - 1]) rows[line - 1].style.opacity = '1';
        }
    }

    // ─────────────────────────────────────────────
    //  PROGRESS BAR & MILESTONES
    // ─────────────────────────────────────────────

    updateProgress(cart) {
        if (!cart) return;
        const totalPrice = cart.total_price;

        const countEl = this.querySelector('#ccd-count');
        if (countEl) countEl.textContent = cart.item_count || 0;

        // Let the theme's header cart bubble update
        document.dispatchEvent(new CustomEvent('cart:update', {
            detail: {
                resource: cart,
                data: { itemCount: cart.item_count, source: 'custom-cart-drawer' }
            }
        }));

        if (!this.offers || this.offers.length === 0) return;
        const sortedOffers = this.offers;

        let percentage    = 0;
        const segmentSize = 100 / sortedOffers.length;
        let prevThreshold = 0;

        for (let i = 0; i < sortedOffers.length; i++) {
            const currentThreshold = sortedOffers[i].threshold;

            if (totalPrice < currentThreshold) {
                const range          = currentThreshold - prevThreshold;
                const spentInSegment = totalPrice - prevThreshold;
                let segmentProgress  = (spentInSegment > 0 ? spentInSegment : 0) / range;
                if (segmentProgress < 0) segmentProgress = 0;
                percentage = (i * segmentSize) + (segmentProgress * segmentSize);
                break;
            } else {
                percentage    = (i + 1) * segmentSize;
                prevThreshold = currentThreshold;
            }
        }

        if (totalPrice >= sortedOffers[sortedOffers.length - 1].threshold) percentage = 100;
        if (percentage > 100) percentage = 100;

        if (!this.progressBar) this.progressBar = this.querySelector('.ccd-progress-fill');
        if (this.progressBar) this.progressBar.style.width = `${percentage}%`;

        const milestones = this.querySelectorAll('.ccd-milestone');
        let currentUnlockedCount = 0;

        milestones.forEach(m => {
            const threshold   = parseInt(m.dataset.threshold, 10);
            const iconDefault = m.querySelector('.icon-default');
            const iconCheck   = m.querySelector('.icon-check');

            if (totalPrice >= threshold) {
                m.classList.add('reached');
                if (iconDefault) iconDefault.style.display = 'none';
                if (iconCheck)   iconCheck.style.display   = 'block';
                currentUnlockedCount++;
            } else {
                m.classList.remove('reached');
                if (iconDefault) iconDefault.style.display = 'block';
                if (iconCheck)   iconCheck.style.display   = 'none';
            }
        });

        if (this.firstLoad) {
            this.lastUnlockedCount = currentUnlockedCount;
            this.firstLoad = false;
        } else {
            if (currentUnlockedCount > this.lastUnlockedCount) this.fireConfetti();
            this.lastUnlockedCount = currentUnlockedCount;
        }

        const nextOffer = sortedOffers.find(offer => offer.threshold > totalPrice);
        let message = '';

        if (nextOffer) {
            const amountRemaining = (nextOffer.threshold - totalPrice) / 100;
            const amountText      = amountRemaining.toLocaleString('en-IN', { maximumFractionDigits: 2 });
            message = `You are ₹${amountText} away from <strong>${nextOffer.rewardText}</strong>!`;
        } else {
            message = "🎉 You've unlocked all offers!";
        }

        if (!this.progressText) this.progressText = this.querySelector('.ccd-progress-text');
        if (this.progressText) this.progressText.innerHTML = message;

        this.manageFreeGifts(sortedOffers, cart);
    }

    // ─────────────────────────────────────────────
    //  FREE GIFT MANAGEMENT
    // ─────────────────────────────────────────────

    async manageFreeGifts(allOffers, cart) {
        if (!allOffers.length) return;
        this.isAutoOperating = true;
        try {
            const totalPrice     = cart.total_price;
            const unlockedOffers = allOffers.filter(offer => offer.threshold <= totalPrice);
            const lockedOffers   = allOffers.filter(offer => offer.threshold > totalPrice);

            let cleanUpNeeded = false;

            for (const offer of lockedOffers) {
                if (offer.type !== 'product' || !offer.variantId) continue;

                const lineItem = cart.items.find(item =>
                    item.variant_id == offer.variantId &&
                    item.properties && (item.properties['_FreeGift'] === 'true' || item.properties['_FreeGift'] === true)
                );

                if (lineItem) {
                    try {
                        const lineIndex = cart.items.indexOf(lineItem) + 1;
                        await fetch('/cart/change.js', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ line: lineIndex, quantity: 0 })
                        });
                        cleanUpNeeded = true;

                        this.removedVariantIds.delete(parseInt(offer.variantId, 10));
                        this.removedVariantIds.delete(String(offer.variantId));
                    } catch (e) { console.error('CCD: Gift removal failed', e); }
                }
            }

            if (cleanUpNeeded) {
                await this.fetchCart();
                return;
            }

            for (const offer of unlockedOffers) {
                if (offer.type === 'product' && offer.variantId) {
                    if (this.removedVariantIds.has(parseInt(offer.variantId, 10)) || this.removedVariantIds.has(String(offer.variantId))) {
                        continue;
                    }

                    const inCart = cart.items.some(item => item.variant_id == offer.variantId);
                    if (!inCart) {
                        try {
                            await fetch('/cart/add.js', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    id: offer.variantId,
                                    quantity: 1,
                                    properties: { '_FreeGift': 'true' }
                                })
                            });
                            await this.fetchCart();
                            return;
                        } catch (e) { console.error('CCD: Auto add failed', e); }
                    }
                }
            }
        } finally {
            this.isAutoOperating = false;
        }
    }

    // ─────────────────────────────────────────────
    //  CONFETTI
    // ─────────────────────────────────────────────

    fireConfetti() {
        const container = document.createElement('div');
        container.className = 'ccd-confetti-container';
        container.style.cssText = 'position:absolute; top:0; left:0; width:100%; height:100%; pointer-events:none; z-index:999; overflow:hidden;';

        (this.drawer || this).appendChild(container);

        for (let i = 0; i < 50; i++) {
            const p = document.createElement('div');
            p.className             = 'ccd-confetti-piece';
            p.style.left            = Math.random() * 100 + '%';
            p.style.animationDelay  = (Math.random() * 2) + 's';
            p.style.backgroundColor = `hsl(${Math.random() * 360}, 100%, 50%)`;
            p.style.width           = (Math.random() * 8 + 5) + 'px';
            p.style.height          = (Math.random() * 8 + 5) + 'px';
            container.appendChild(p);
        }

        setTimeout(() => { container.remove(); }, 5000);
    }

    // ─────────────────────────────────────────────
    //  COUPONS
    // ─────────────────────────────────────────────

    initCoupons() {
        const input         = this.querySelector('#ccd-coupon-input');
        const applyBtn      = this.querySelector('#ccd-apply-coupon');
        const toggleBtn     = this.querySelector('#ccd-toggle-coupons');
        const listContainer = this.querySelector('#ccd-coupon-list');
        const countLabel    = this.querySelector('#ccd-coupon-count');

        let coupons = [];
        try {
            const script = this.querySelector('#ccd-coupons-data');
            if (script) coupons = JSON.parse(script.textContent);
        } catch (e) { console.error('CCD: Coupon parse error', e); }

        if (countLabel) countLabel.innerText = `${coupons.length} coupons available`;

        if (listContainer) {
            listContainer.innerHTML = '';
            coupons.forEach(c => {
                const row = document.createElement('div');
                row.style.cssText = 'display:flex; justify-content:space-between; margin-bottom:8px; align-items:center; border-bottom:1px solid #eee; padding-bottom:5px;';
                row.innerHTML = `
                    <div>
                       <div style="font-weight:700; color:#000;">${c.code}</div>
                       <div style="font-size:11px; color:#555;">${c.description}</div>
                    </div>
                    <button type="button" class="ccd-use-coupon" data-code="${c.code}" style="background:#000; color:#fff; border:none; border-radius:4px; padding:4px 8px; font-size:10px; cursor:pointer;">APPLY</button>
                `;
                listContainer.appendChild(row);
            });

            if (coupons.length === 0 && toggleBtn) {
                toggleBtn.style.display = 'none';
                if (countLabel) countLabel.innerText = '';
            }
        }

        if (toggleBtn && listContainer && toggleBtn.dataset.ccdBound !== '1') {
            toggleBtn.dataset.ccdBound = '1';
            toggleBtn.addEventListener('click', (e) => {
                e.preventDefault();
                listContainer.style.display = listContainer.style.display === 'none' ? 'block' : 'none';
            });
        }

        // (.ccd-use-coupon clicks are handled once in handleClick)

        if (applyBtn && applyBtn.dataset.ccdBound !== '1') {
            applyBtn.dataset.ccdBound = '1';
            applyBtn.addEventListener('click', (e) => {
                e.preventDefault();
                const code = input ? input.value.trim() : '';
                if (code) this.applyCoupon(code);
            });
        }

        const activeCode = sessionStorage.getItem('ccd_discount_code');
        if (activeCode && input) {
            input.value = activeCode;
            this.showCouponSuccess(activeCode);
        }
    }

    applyCoupon(code) {
        sessionStorage.setItem('ccd_discount_code', code);
        this.showCouponSuccess(code);
    }

    showCouponSuccess(code) {
        const message = this.querySelector('#ccd-coupon-message');
        const input   = this.querySelector('#ccd-coupon-input');
        if (message) {
            message.style.display = 'block';
            message.style.color   = '#00b894';
            message.innerHTML     = `Discount code <strong>${code}</strong> applied! It will be calculated at checkout.`;
        }
        if (input) {
            input.disabled         = true;
            input.style.color      = '#00b894';
            input.style.fontWeight = '700';
        }
    }
}

if (!customElements.get('custom-cart-drawer')) {
    customElements.define('custom-cart-drawer', CustomCartDrawer);
}